// Match row — the central atom of Today + Matches + CVPicker.
//
// Layout (left → right):
//   [ Company / chips / role / tl;dr / must-haves ]   [ Score / actions ]
//
// The leading first-letter LogoSquare and the checkbox were removed in
// favour of a single "Add to CV" button in the action cluster — same
// affordance the user wanted ("一个UI能直接把他转到CV"), one click,
// no visual clutter. Selected state shows ✓ + primary-green so it's
// instantly readable across a long list.
//
// Delete uses inline two-click confirm: first click turns the trash
// icon red + shows "×" (3s auto-revert), second click fires onDelete.

import { useEffect, useState } from 'react';
import { Trash2, X, FileText, Check } from 'lucide-react';
import Chip, { fitVariant } from '../ui/Chip';
import DeadlineChip from '../ui/DeadlineChip';
import type { Match } from '../../lib/types';
import { useZh } from '../../lib/useProduct';
import { fitTierLabels } from '../../lib/fitTiers';

interface Props {
  match: Match;
  onOpen?: (m: Match) => void;
  onDelete?: (m: Match) => void;
  // Optional CV-pool toggle. When provided, MatchRow renders an
  // "Add to CV" button in the right action cluster. Clicking it
  // toggles the match in/out of the CV-tailoring pool that drives
  // /cv. Legacy mount sites (CVPicker itself, Today page) omit this
  // prop so their action clusters stay unchanged.
  onToggleCv?: (m: Match, next: boolean) => void;
  // Optional manual mark-as-expired callback. When provided, the
  // LivenessChip renders a "✗ Mark expired" link for unknown / live
  // entries — used for hosts (Indeed/Glassdoor) where the auto-sweep
  // can't probe due to CF-blocking. The user verifies in their own
  // browser, then flips the chip from here. Cascades same side
  // effects as auto-sweep expiry: blocklist + cv-selections cleanup.
  onMarkExpired?: (m: Match) => void;
}

export default function MatchRow({ match: m, onOpen, onDelete, onToggleCv, onMarkExpired }: Props) {
  const zh = useZh();
  const scoreColor = m.score >= 85 ? 'var(--pri)' : m.score >= 75 ? 'var(--ink)' : 'var(--ink2)';
  const [armed, setArmed] = useState(false);

  // Auto-revert the armed state after 3s so accidental single-clicks don't sit
  // permanently waiting for a second click
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDelete) return;
    if (armed) {
      onDelete(m);
      setArmed(false);
    } else {
      setArmed(true);
    }
  };

  const handleCvToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onToggleCv) return;
    onToggleCv(m, !m.selected_for_cv);
  };

  return (
    <div className="match-row" onClick={() => onOpen?.(m)}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>{m.company}</div>
          {/* Same vocabulary as the tier filter chips above the list — this
              badge used to render the raw enum ("Primary"), so one screen
              described the same thing as both 强匹配 and Primary. */}
          <Chip variant={fitVariant(m.fit)} style={{ fontSize: 10.5 }}>
            {fitTierLabels(zh)[m.fit]?.label ?? m.fit}
          </Chip>
          {/* Auto-discarded chip — legacy LLM pre-screen reports
              (pre-screen disabled 2026-05-19; no new writers). Hover
              shows the historical reason; deleting the report.md
              pushes the URL back into Pending. */}
          {m.status === 'auto-discarded' && (
            <span
              className="chip"
              style={{
                fontSize: 10.5,
                padding: '2px 7px',
                background: 'rgba(110,110,110,0.08)',
                color: 'var(--ink3)',
                fontStyle: 'italic',
              }}
              title={m.screen_reason || (zh ? '预筛判断不值得完整评估' : 'Pre-screen judged not worth a full evaluation')}
            >
              LLM-skipped
            </span>
          )}
          {/* Reputation chip — LLM-scored 0-100 at evaluation time. Hidden
              when null (older reports without Block H). The 80/60 thresholds
              match the rubric in modes/oferta.md so the visual stays in sync
              with how the LLM is asked to score. */}
          {typeof m.reputation === 'number' && (
            <span
              className="chip"
              style={{
                fontSize: 10.5,
                padding: '2px 7px',
                background:
                  m.reputation >= 80 ? 'rgba(40,120,60,0.10)'
                  : m.reputation >= 60 ? 'rgba(170,140,30,0.10)'
                  : 'rgba(110,110,110,0.08)',
                color:
                  m.reputation >= 80 ? '#1f6f3a'
                  : m.reputation >= 60 ? '#8a6f1c'
                  : 'var(--ink2)',
              }}
              title={zh ? '公司声誉（评估时由 LLM 打分）' : 'Company reputation (LLM-scored at evaluation)'}
            >
              {zh ? '声誉' : 'Rep'} {m.reputation}
            </span>
          )}
          <ClosesChip closesAt={m.closes_at} />
          {/* Liveness chip — written by the daily liveness sweep
              (web/lib/liveness-sweep.mjs) into data/liveness-cache.json
              and merged into /api/matches. Shows "Expired" in red when
              the URL no longer resolves to an active posting, "Live"
              softly in grey when last check confirmed it. Hidden when
              the row was never checked (liveness === 'unknown') so the
              UI stays clean for fresh scans before the first sweep. */}
          <LivenessChip
            state={m.liveness}
            checkedAt={m.liveness_checked_at}
            url={m.url}
            matchId={m.id}
            onMarkExpired={onMarkExpired}
          />
          <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginLeft: 'auto' }}>{m.posted}</div>
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--ink)', marginTop: 3 }}>{m.role}</div>
        {/* Role TL;DR — 1-2 sentence "what is this job" line surfaced from
            the Summary Card written by the LLM at evaluation time. Truncated
            to 2 lines via line-clamp; full text shows on hover. Hidden when
            the field is null (reports written before Phase 2). */}
        {m.role_tldr && (
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--ink2)',
              marginTop: 4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.4,
            }}
            title={m.role_tldr}
          >
            {m.role_tldr}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 4 }}>
          {m.location} · {m.comp}
        </div>
        {/* Must-have requirement chips — first 2 hard requirements, subtle
            grey style so they don't compete with the fit/reputation chips
            in the top row. Only renders when the report has them
            extracted; older reports without must_haves stay clean. */}
        {m.must_haves && m.must_haves.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {m.must_haves.slice(0, 2).map((r) => (
              <span
                key={r}
                className="tag"
                style={{
                  fontSize: 10.5,
                  background: 'rgba(110,110,110,0.08)',
                  color: 'var(--ink2)',
                }}
                title={`Must-have: ${r}`}
              >
                {r.length > 60 ? r.slice(0, 57) + '…' : r}
              </span>
            ))}
          </div>
        )}
        {m.reasons.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {m.reasons.map((r) => (
              <span key={r} className="tag">
                {r}
              </span>
            ))}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', minWidth: 100 }}>
        <div className="num" style={{ fontSize: 34, lineHeight: 1, color: scoreColor }}>
          {m.score}
        </div>
        <div
          style={{
            fontSize: 10.5,
            color: 'var(--ink3)',
            marginTop: 2,
            letterSpacing: 0.05,
            textTransform: 'uppercase',
          }}
        >
          {zh ? '匹配分' : 'Fit'}
        </div>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 10, flexWrap: 'wrap' }}>
          {/* "Save for CV tailoring" — the single affordance for adding
              this match to the queue on /cv that gets a custom CV
              generated per role. Original "Add to CV" wording was
              ambiguous (was "CV" my own resume? was I editing it?).
              "Save for CV tailoring" spells out (a) the action is just
              a save / earmark, (b) the destination is the tailoring
              page. Selected state shrinks to "✓ Saved" once the user
              knows what's queued.
              Renders only when onToggleCv was supplied — keeps
              CVPicker / Today pages clean. */}
          {onToggleCv && (
            <button
              className="btn"
              onClick={handleCvToggle}
              style={{
                fontSize: 11.5,
                padding: '4px 10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                color: m.selected_for_cv ? '#fff' : 'var(--ink)',
                background: m.selected_for_cv ? 'var(--pri)' : 'transparent',
                border: m.selected_for_cv
                  ? '1px solid var(--pri)'
                  : '1px solid var(--line)',
                transition: 'background 120ms, color 120ms, border-color 120ms',
              }}
              title={
                m.selected_for_cv
                  ? (zh ? '已加入「简历定制」——再点一次移除' : 'Saved for CV tailoring — click to remove from the /cv page queue')
                  : (zh ? '收藏这个岗位，之后可以在「简历定制」里生成一份针对它要求的简历。' : 'Save this role so the /cv page can generate a custom CV tailored to its requirements. Apply faster.')
              }
              aria-pressed={!!m.selected_for_cv}
            >
              {m.selected_for_cv ? <Check size={13} /> : <FileText size={13} />}
              {m.selected_for_cv ? (zh ? '已收藏' : 'Saved') : (zh ? '收藏做简历' : 'Save for CV')}
            </button>
          )}
          {/* "Open link" — opens the external JD URL in a new tab.
              Row click (the parent div) still navigates to the in-app
              report at /job/{id}. stopPropagation is essential here so
              clicking the link doesn't ALSO trigger the row's onOpen.
              Defensive: if no URL, render an inert grey label. */}
          {m.url ? (
            <a
              className="btn ghost"
              href={m.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ fontSize: 11.5, textDecoration: 'none' }}
              title={zh ? '在新标签页打开原始职位页面' : 'Open the original job posting in a new tab'}
            >
              {zh ? '原职位 ↗' : 'Open ↗'}
            </a>
          ) : (
            <span
              className="btn ghost"
              style={{ fontSize: 11.5, opacity: 0.4, cursor: 'not-allowed' }}
              title={zh ? '这个职位没有链接' : 'No URL available for this posting'}
            >
              No link
            </span>
          )}
          {onDelete && (
            <button
              className="btn ghost"
              onClick={handleDeleteClick}
              style={{
                fontSize: 11.5,
                padding: '4px 10px',
                color: armed ? '#fff' : '#b43a3a',
                background: armed ? '#b43a3a' : 'transparent',
                border: armed ? '1px solid #b43a3a' : undefined,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 120ms',
              }}
              title={armed
                ? (zh ? '再点一次永久删除' : 'Click again to permanently delete')
                : (zh ? '删除这份评估报告' : 'Delete this evaluation report')}
              aria-label={armed ? (zh ? '确认删除' : 'Confirm delete') : (zh ? '删除' : 'Delete')}
            >
              {armed ? (
                <>
                  <X size={13} /> Sure?
                </>
              ) : (
                <Trash2 size={13} />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ClosesChip — application deadline ("Closes in 3 days" / "Closed 2 days ago")
// ────────────────────────────────────────────────────────────────────────────
//
// Hidden when closes_at is null (most reports — JD body had no deadline cue,
// JD body wasn't fetched, or report pre-dates the feature). Color-coded by
// urgency:
//
//   < 0 days (past)    → grey "Closed N d ago"  — informational only
//   ≤ 3 days           → red   "Closes in N d"  — urgent
//   ≤ 7 days           → amber "Closes in N d"  — soon
//   > 7 days           → green "Closes in N d"  — comfortable
//
// We compute days against the user's local clock (Date.now()) rather than
// the server clock to avoid timezone drift confusion. closes_at is plain
// YYYY-MM-DD which Date treats as midnight UTC — close enough for a "days
// remaining" display where rounding by 24h is fine.

// ────────────────────────────────────────────────────────────────────────────
// LivenessChip — "Live" / "Expired" badge from the daily liveness sweep
// ────────────────────────────────────────────────────────────────────────────
//
// Hidden when state is 'unknown' or undefined (the row was never checked, or
// the report pre-dates the sweep). When 'expired', renders a red chip — the
// user shouldn't waste time clicking through. When 'live', renders a subtle
// grey "Live" so it doesn't compete visually with the score/fit chips; the
// chip is only really useful when it says Expired, but showing Live on the
// most-recently-checked rows reassures the user the sweep is working.

function LivenessChip({
  state,
  checkedAt,
  url,
  matchId,
  onMarkExpired,
}: {
  state?: 'live' | 'expired' | 'unknown';
  checkedAt?: string | null;
  url?: string | null;
  matchId?: string;
  onMarkExpired?: (m: Match) => void;
}) {
  const zh = useZh();
  // 'unknown' state used to render NOTHING — invisible to the user, so
  // Indeed/Glassdoor entries (which always come back unknown because
  // Cloudflare 403s the VM IP at the network layer) silently looked
  // "live". Now we surface a small "?" chip with a "Mark expired" link
  // so the user can flip them manually after verifying in their own
  // browser. The cascade (blocklist + cv-selections cleanup) fires the
  // same as auto-sweep-detected expiries.
  if (!state) return null;
  const ageLabel = checkedAt ? formatAge(checkedAt) : '';

  if (state === 'unknown') {
    if (!onMarkExpired || !matchId) return null;
    return (
      <span
        className="chip"
        style={{
          fontSize: 10.5,
          padding: '2px 7px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: 'rgba(170,140,30,0.08)',
          color: '#8a6f1c',
        }}
        title={zh
          ? `无法自动确认这个职位还在不在（通常是被 Cloudflare 挡住了）。${ageLabel ? '上次尝试是 ' + ageLabel + '前。' : ''}`
          : `Liveness can't be auto-verified (often Cloudflare-blocked).${ageLabel ? ' Last attempted ' + ageLabel + ' ago.' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {zh ? '? 待确认' : '? unverified'}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#8a6f1c', textDecoration: 'underline', fontSize: 10 }}
            title={zh ? '在新标签页打开职位页面，自己确认一下' : 'Open the posting in a new tab to check yourself'}
          >
            {zh ? '去看看' : 'check'}
          </a>
        )}
        <button
          onClick={() => {
            // The onMarkExpired callback resolves the match by id at the
            // call site (Matches.tsx); pass a sparse stub so the prop's
            // signature stays a Match.
            onMarkExpired({ id: matchId } as Match);
          }}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#a13030',
            cursor: 'pointer',
            fontSize: 10,
            padding: 0,
            textDecoration: 'underline',
          }}
          title={zh ? '标记为已过期（会屏蔽该链接并移出简历定制列表）' : 'Mark this posting as expired (will blocklist the URL + remove from CV pool)'}
        >
          {zh ? '标记过期' : 'mark expired'}
        </button>
      </span>
    );
  }

  const isExpired = state === 'expired';
  const title = zh
    ? `这个职位${isExpired ? '已经过期' : '还在招'}${ageLabel ? ' · ' + ageLabel + '前检查过' : ''}`
    : `Posting ${isExpired ? 'is expired' : 'is live'}${ageLabel ? ' · checked ' + ageLabel + ' ago' : ''}`;
  return (
    <span
      className="chip"
      style={{
        fontSize: 10.5,
        padding: '2px 7px',
        background: isExpired ? 'rgba(180,58,58,0.10)' : 'rgba(40,120,60,0.08)',
        color: isExpired ? '#a13030' : '#1f6f3a',
      }}
      title={title}
    >
      {isExpired ? (zh ? '已过期' : 'Expired') : (zh ? '在招' : 'Live')}
    </span>
  );
}

function formatAge(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const ms = Date.now() - t;
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours >= 1) return `${hours}h`;
  const mins = Math.floor(ms / (1000 * 60));
  return `${mins}m`;
}

function ClosesChip({ closesAt }: { closesAt?: string | null }) {
  // Delegates to the shared DeadlineChip — countdown logic + urgency
  // colours live there now, shared with CV Writer's manual deadline.
  return <DeadlineChip date={closesAt} />;
}
